import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmptyState, StatusBadge, inr } from "@/components/bits";
import api, { errMsg, fmtDate } from "@/lib/api";

export default function AdminPayouts() {
  const [payouts, setPayouts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [invoices, setInvoices] = useState([]);

  const load = () => {
    api.get("/admin/payouts").then(({ data }) => setPayouts(data.payouts)).catch(() => {});
    api.get("/admin/transactions").then(({ data }) => {
      setTransactions(data.transactions);
      setInvoices(data.invoices);
    }).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/admin/payouts/${id}/status`, { status });
      toast.success(`Payout ${status.replace(/_/g, " ")}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="admin-payouts-page">
      <Tabs defaultValue="payouts">
        <TabsList className="mb-6">
          <TabsTrigger value="payouts" data-testid="tab-payouts">Payouts</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="tab-transactions">Payment Transactions</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
        </TabsList>

        <TabsContent value="payouts">
          {payouts.length === 0 ? (
            <EmptyState title="No payouts yet" description="Partner and institute payouts appear here as placements and enrollments happen." testId="payouts-empty" />
          ) : (
            <div className="space-y-3">
              {payouts.map((p) => (
                <div key={p.id} className="bg-white border border-border/60 rounded-xl p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`payout-${p.id}`}>
                  <div>
                    <p className="font-semibold text-sm text-[#0B1B3D]">
                      {p.payee_name} <span className="text-xs text-muted-foreground font-normal">({p.payee_type})</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {p.ref?.candidate || p.ref?.course_title} {p.ref?.job_title && `· ${p.ref.job_title}`} · {fmtDate(p.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-heading font-bold">{inr(p.amount)}</span>
                    <StatusBadge status={p.status} />
                    {["pending", "eligible"].includes(p.status) && (
                      <Button size="sm" className="rounded-full bg-[#2563EB]" onClick={() => setStatus(p.id, "approved")} data-testid={`approve-payout-${p.id}`}>Approve</Button>
                    )}
                    {p.status === "approved" && (
                      <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => setStatus(p.id, "paid")} data-testid={`pay-payout-${p.id}`}>Mark Paid</Button>
                    )}
                    {!["paid", "rejected"].includes(p.status) && (
                      <Button size="sm" variant="outline" className="rounded-full text-orange-700 border-orange-200" onClick={() => setStatus(p.id, "on_hold")} data-testid={`hold-payout-${p.id}`}>Hold</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="transactions">
          {transactions.length === 0 ? (
            <EmptyState title="No transactions yet" testId="transactions-empty" />
          ) : (
            <div className="bg-white border border-border/60 rounded-xl overflow-x-auto">
              <table className="w-full text-sm" data-testid="transactions-table">
                <thead className="bg-[#F8FAFC] border-b border-border/60 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-5 py-3.5 font-semibold">Description</th>
                    <th className="px-5 py-3.5 font-semibold">Kind</th>
                    <th className="px-5 py-3.5 font-semibold">Amount</th>
                    <th className="px-5 py-3.5 font-semibold">Status</th>
                    <th className="px-5 py-3.5 font-semibold">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((t) => (
                    <tr key={t.id} className="border-b border-border/40">
                      <td className="px-5 py-4">{t.description}</td>
                      <td className="px-5 py-4 text-xs capitalize">{t.kind}</td>
                      <td className="px-5 py-4 font-semibold">{inr(t.amount)}</td>
                      <td className="px-5 py-4"><StatusBadge status={t.payment_status} /></td>
                      <td className="px-5 py-4 text-xs text-muted-foreground">{fmtDate(t.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="invoices">
          {invoices.length === 0 ? (
            <EmptyState title="No invoices yet" testId="invoices-empty" />
          ) : (
            <div className="bg-white border border-border/60 rounded-xl overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-invoices-table">
                <thead className="bg-[#F8FAFC] border-b border-border/60 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-5 py-3.5 font-semibold">Invoice</th>
                    <th className="px-5 py-3.5 font-semibold">Description</th>
                    <th className="px-5 py-3.5 font-semibold">Total</th>
                    <th className="px-5 py-3.5 font-semibold">Status</th>
                    <th className="px-5 py-3.5 font-semibold">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((i) => (
                    <tr key={i.id} className="border-b border-border/40">
                      <td className="px-5 py-4 font-mono text-xs font-semibold">{i.invoice_no}</td>
                      <td className="px-5 py-4">{i.description}</td>
                      <td className="px-5 py-4 font-bold">{inr(i.total)}</td>
                      <td className="px-5 py-4"><StatusBadge status={i.status} /></td>
                      <td className="px-5 py-4 text-xs text-muted-foreground">{fmtDate(i.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
